/**
 * STL (Stereolithography) Exporter
 * Exports Three.js geometry/mesh to binary STL format
 *
 * IMPORTANT: STL Format Details
 * - Binary format for efficient storage
 * - No color support (use PLY for colors)
 * - Each triangle stores its own FACE NORMAL (not vertex normals)
 * - Face normals are computed from vertex positions using right-hand rule
 * - This is critical for correct import/re-export cycles
 *
 * Unit note: `meshToSTL` writes vertex coordinates as-is after baking the mesh world matrix.
 * For BK editor meshes (1 scene unit = 1 cm), use `exportMesh` / `downloadMesh` from
 * `./modelExporter.js`, which scales vertices to millimeters for slicers.
 */

import * as THREE from 'three';

/**
 * Convert Three.js geometry to binary STL format
 * @param {THREE.Geometry|THREE.BufferGeometry} geometry - The geometry to export
 * @param {Object} options - Export options
 * @returns {ArrayBuffer} Binary STL data
 */
export function geometryToSTL(geometry, options = {}) {
  // Ensure we have a BufferGeometry
  let bufferGeometry = geometry;
  if (!(geometry instanceof THREE.BufferGeometry)) {
    bufferGeometry = new THREE.BufferGeometry().fromGeometry(geometry);
  }

  // Create a copy to avoid modifying the original
  bufferGeometry = bufferGeometry.clone();

  // CRITICAL: Always compute vertex normals for STL export
  // This ensures normals are properly computed from face geometry
  // even if they already exist (they may be incorrect from the original model)
  bufferGeometry.computeVertexNormals();

  const positions = bufferGeometry.getAttribute('position');
  const normals = bufferGeometry.getAttribute('normal');
  const indices = bufferGeometry.getIndex();

  if (!positions) {
    throw new Error('Geometry must have position attribute');
  }

  // Calculate number of triangles
  let triangles = 0;
  if (indices) {
    triangles = indices.count / 3;
  } else {
    triangles = positions.count / 3;
  }

  // Create binary STL data
  // Header: 80 bytes (can be any data)
  // Triangle count: 4 bytes (uint32)
  // For each triangle:
  //   - Normal vector: 12 bytes (3 floats)
  //   - Vertex 1: 12 bytes (3 floats)
  //   - Vertex 2: 12 bytes (3 floats)
  //   - Vertex 3: 12 bytes (3 floats)
  //   - Attribute byte count: 2 bytes (uint16) - usually 0

  const arrayBuffer = new ArrayBuffer(80 + 4 + triangles * 50);
  const view = new DataView(arrayBuffer);
  const pos = new THREE.Vector3();
  const normal = new THREE.Vector3();

  // Header
  const headerText = 'Three.js STL Export';
  const headerBytes = new Uint8Array(arrayBuffer, 0, 80);
  for (let i = 0; i < Math.min(headerText.length, 80); i++) {
    headerBytes[i] = headerText.charCodeAt(i);
  }

  // Triangle count
  view.setUint32(80, triangles, true); // true = little-endian

  // Write triangles
  let offset = 84;
  const vertexA = new THREE.Vector3();
  const vertexB = new THREE.Vector3();
  const vertexC = new THREE.Vector3();
  const faceNormal = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();

  for (let i = 0; i < triangles; i++) {
    const a = indices ? indices.getX(i * 3) : i * 3;
    const b = indices ? indices.getX(i * 3 + 1) : i * 3 + 1;
    const c = indices ? indices.getX(i * 3 + 2) : i * 3 + 2;

    // Get vertices
    vertexA.fromBufferAttribute(positions, a);
    vertexB.fromBufferAttribute(positions, b);
    vertexC.fromBufferAttribute(positions, c);

    // Compute face normal from triangle vertices
    // This is the proper STL normal (face normal, not vertex normal)
    ab.subVectors(vertexB, vertexA);
    ac.subVectors(vertexC, vertexA);
    faceNormal.crossVectors(ab, ac).normalize();

    // Write normal (STL uses face normals, not vertex normals)
    view.setFloat32(offset, faceNormal.x, true);
    offset += 4;
    view.setFloat32(offset, faceNormal.y, true);
    offset += 4;
    view.setFloat32(offset, faceNormal.z, true);
    offset += 4;

    // Write vertices
    view.setFloat32(offset, vertexA.x, true);
    offset += 4;
    view.setFloat32(offset, vertexA.y, true);
    offset += 4;
    view.setFloat32(offset, vertexA.z, true);
    offset += 4;

    view.setFloat32(offset, vertexB.x, true);
    offset += 4;
    view.setFloat32(offset, vertexB.y, true);
    offset += 4;
    view.setFloat32(offset, vertexB.z, true);
    offset += 4;

    view.setFloat32(offset, vertexC.x, true);
    offset += 4;
    view.setFloat32(offset, vertexC.y, true);
    offset += 4;
    view.setFloat32(offset, vertexC.z, true);
    offset += 4;

    // Attribute byte count (unused)
    view.setUint16(offset, 0, true);
    offset += 2;
  }

  return arrayBuffer;
}

/**
 * Export Three.js mesh to STL format (vertex units unchanged except world matrix bake).
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {Object} options - Export options
 * @returns {ArrayBuffer} Binary STL data
 */
export function meshToSTL(mesh, options = {}) {
  if (!mesh || !mesh.geometry) {
    throw new Error('Invalid mesh provided');
  }

  // Bake world transform so STL matches what you see (position/rotation/scale on the mesh).
  const bufferGeometry = mesh.geometry.clone();
  mesh.updateWorldMatrix(true, false);
  bufferGeometry.applyMatrix4(mesh.matrixWorld);

  return geometryToSTL(bufferGeometry, options);
}

/**
 * Create Blob from STL data
 * @param {ArrayBuffer} stlData - STL binary data
 * @returns {Blob} STL blob
 */
export function createSTLBlob(stlData) {
  return new Blob([stlData], { type: 'application/octet-stream' });
}

/**
 * Download helper around `meshToSTL` (no cm→mm scaling).
 * For BK editor geometry in centimeters, use `downloadMesh` from `./modelExporter.js`.
 *
 * @param {THREE.Mesh} mesh - The mesh to export
 * @param {string} filename - Filename for download
 * @param {Object} options - Export options
 */
export function exportMeshToSTL(mesh, filename, options = {}) {
  try {
    const stlData = meshToSTL(mesh, options);
    const blob = createSTLBlob(stlData);
    downloadBlob(blob, filename);
  } catch (error) {
    console.error('Error exporting to STL:', error);
    throw error;
  }
}

/**
 * Helper function to trigger file download
 * @param {Blob} blob - Data blob
 * @param {string} filename - Filename
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
